import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { ChevronDown } from "lucide-react";

export default function PortalDropdownBox({
  optionList = [],
  value = "",
  handleChange,
  placeholder = "Select an option",
  styleName = "",
  menuClassName = "",
  maxHeight = "300px",
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPos, setMenuPos] = useState(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null); 

  // Toggle dropdown and calculate position
  const toggleDropdown = () => {
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPos({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
    setIsOpen(!isOpen);
  };

  // Close when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(event.target) &&
        menuRef.current &&
        !menuRef.current.contains(event.target) 
      ) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close if user scrolls
  useEffect(() => {
    if (!isOpen) return;
    const close = () => setIsOpen(false);
    window.addEventListener("scroll", close, true);
    return () => window.removeEventListener("scroll", close, true);
  }, [isOpen]);

  const menuContent = (
    <div
      ref={menuRef} 
      className={`bg-white border border-gray-300 rounded-md shadow-lg ${menuClassName}`}
      style={{
        position: "absolute",
        top: menuPos?.top,
        left: menuPos?.left,
        width: menuPos?.width,
        maxHeight,
        overflowY: "auto",
        zIndex: 9999,
      }}
    >
      {optionList.length > 0 ? (
        optionList.map((opt) => (
          <div
            key={opt}
            className={`px-4 py-2 text-gray-700 hover:bg-gray-100 cursor-pointer ${
              opt === value ? "bg-gray-50" : ""
            }`}
            onClick={() => {
              handleChange(opt);
              setIsOpen(false);
            }}
          >
            {opt}
          </div>
        ))
      ) : (
        <div className="px-4 py-2 text-gray-500 italic">No options</div>
      )}
    </div>
  );

  return (
    <div className="relative w-full">
      <button
        ref={buttonRef}
        type="button"
        onClick={toggleDropdown}
        className={`flex items-center justify-between border bg-white px-4 py-2 w-full text-left border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-0 ${styleName}`}
      >
        <span>{value || placeholder}</span>
        <ChevronDown className={`w-5 h-5 ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {isOpen && createPortal(menuContent, document.body)}
    </div>
  );
}
